import React, { useState, useEffect } from 'react';
import './InfluencerPrompt.css';
import { influencerService, getInfluencerImageUrl } from '../services/influencerApi';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services/authService';

function InfluencerPrompt() {
  const { credits, isAdmin, refreshCredits } = useAuth();
  const [characters, setCharacters] = useState([]);
  const [environments, setEnvironments] = useState([]);
  const [sceneDescription, setSceneDescription] = useState('');
  const [characterSearch, setCharacterSearch] = useState('');
  const [environmentSearch, setEnvironmentSearch] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState('');
  const [enhancedPrompt, setEnhancedPrompt] = useState('');
  const [error, setError] = useState('');
  const [generatedVideoPath, setGeneratedVideoPath] = useState('');
  const [videoDuration, setVideoDuration] = useState(10);

  useEffect(() => {
    loadLibraries();
  }, []);

  const loadLibraries = async () => {
    try {
      const [charResponse, envResponse] = await Promise.all([
        influencerService.getCharacterLibrary(),
        influencerService.getBackgroundLibrary()
      ]);

      if (charResponse.success && charResponse.characters) {
        setCharacters(charResponse.characters.map((char, index) => ({
          ...char,
          name: extractNameFromFilename(char.filename),
          selected: index === 0
        })));
      }

      if (envResponse.success && envResponse.backgrounds) {
        setEnvironments(envResponse.backgrounds.map((env, index) => ({
          ...env,
          name: extractNameFromFilename(env.filename),
          selected: index === 0
        })));
      }
    } catch (error) {
      console.error('Failed to load libraries:', error);
      setError('Failed to load character and environment libraries');
    }
  };

  const extractNameFromFilename = (filename) => {
    const nameWithExt = filename.split('/').pop() || filename;
    const nameWithoutExt = nameWithExt.replace(/\.(png|jpg|jpeg)$/i, '');
    const parts = nameWithoutExt.split('_');
    if (parts.length > 1) {
      return parts[0].replace(/-/g, ' ');
    }
    return nameWithoutExt.replace(/-/g, ' ');
  };

  const toggleCharacter = (filename) => {
    setCharacters(
      characters.map((c) =>
        c.filename === filename ? { ...c, selected: !c.selected } : c
      )
    );
  };

  const toggleEnvironment = (filename) => {
    setEnvironments(
      environments.map((e) => ({ ...e, selected: e.filename === filename }))
    );
  };

  const filteredCharacters = characters.filter(char =>
    char.name.toLowerCase().includes(characterSearch.toLowerCase())
  );

  const filteredEnvironments = environments.filter(env =>
    env.name.toLowerCase().includes(environmentSearch.toLowerCase())
  );

  const pollJobStatus = async (jobId) => {
    const maxAttempts = 180; // Longer timeout for multi-clip videos
    let attempts = 0;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        throw new Error('Video generation timeout. Please try again.');
      }
      attempts++;

      try {
        const data = await influencerService.checkJobStatus(jobId);

        if (data.status === 'completed') {
          const videoPath = data.video_path || data.public_video_url || data.download_url || `/public/videos/${jobId}.mp4`;
          setGeneratedVideoPath(videoPath);
          setGenerationProgress('Video generation completed! Video saved to library.');
          setTimeout(() => setGenerationProgress(''), 5000);
          return;
        } else if (data.status === 'failed') {
          throw new Error(data.message || data.error || 'Video generation failed');
        } else {
          const progress = data.progress || 0;
          const msg = data.message || `Generating video... (${progress}%)`;
          setGenerationProgress(msg);
          await new Promise(resolve => setTimeout(resolve, 5000));
          return poll();
        }
      } catch (error) {
        // If it's a 404 (job not found yet), retry
        if (error?.response?.status === 404 && attempts < 5) {
          await new Promise(resolve => setTimeout(resolve, 3000));
          return poll();
        }
        console.error('Polling error:', error);
        throw error;
      }
    };

    await poll();
  };

  const handleGenerateVideo = async () => {
    setError('');
    setEnhancedPrompt('');
    setGenerationProgress('');
    setGeneratedVideoPath('');
    setIsGenerating(true);

    try {
      const selectedCharacter = characters.find(c => c.selected);
      const selectedEnvironment = environments.find(e => e.selected);

      if (!selectedCharacter || !selectedEnvironment) {
        setError('Please select both a character and an environment');
        setIsGenerating(false);
        return;
      }

      if (!sceneDescription.trim()) {
        setError('Please describe your scene');
        setIsGenerating(false);
        return;
      }

      // Check and deduct credit before generating (non-admins only)
      if (!isAdmin) {
        if (credits < 1) {
          setError('You have no credits remaining. Please request more credits from admin.');
          setIsGenerating(false);
          return;
        }
        try {
          await authService.useCredit();
          refreshCredits();
        } catch (creditErr) {
          setError(creditErr?.response?.data?.detail || 'Failed to deduct credit. Please try again.');
          setIsGenerating(false);
          return;
        }
      }

      // Choose API based on duration
      let data;
      if (videoDuration === 10) {
        // Standard 10s video — uses generate-video (synchronous)
        setGenerationProgress('Generating 10s video...');
        data = await influencerService.generateVideoFromPaths(
          selectedCharacter.path,
          selectedEnvironment.path,
          sceneDescription,
          10,
          '1280*720'
        );
      } else {
        // Multi-clip video (30s or 40s) — uses generate-video-v2
        setGenerationProgress(`Starting ${videoDuration}s multi-clip video generation...`);
        data = await influencerService.generateVideoV2(
          selectedCharacter.path,
          selectedEnvironment.path,
          sceneDescription,
          videoDuration,
          '1280*720'
        );
      }

      if (data.success && (data.video_path || data.job_id)) {
        if (data.video_path) {
          // Synchronous response — video is already ready
          setGeneratedVideoPath(data.video_path);
          if (data.enhanced_prompt) setEnhancedPrompt(data.enhanced_prompt);
          setGenerationProgress('Video generation completed!');
          setTimeout(() => setGenerationProgress(''), 5000);
        } else if (data.job_id) {
          // v2 API — poll for status since it's a longer process
          await pollJobStatus(data.job_id);
        }
      } else {
        throw new Error(data.error || 'Video generation failed');
      }
    } catch (error) {
      console.error('Video generation error:', error);
      setError(error.message || 'Failed to generate video. Please try again.');
      setGenerationProgress('');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="influencer-prompt">
      <div className="header">
        <h1>AI Influencer Video</h1>
        <p className="subtitle">Select characters & environments, then describe your scene to generate AI-powered videos</p>
      </div>

      <div className="prompt-layout">
        {/* Left Column - Characters & Environments */}
        <div className="selection-column">
          {/* Characters Section */}
          <div className="selection-card">
            <h2>1. Select Characters</h2>
            <div className="search-box compact">
              <i className="fas fa-search"></i>
              <input
                type="text"
                placeholder="Search characters..."
                value={characterSearch}
                onChange={(e) => setCharacterSearch(e.target.value)}
              />
            </div>
            <div className="selection-list">
              {filteredCharacters.map((character) => (
                <button
                  key={character.filename}
                  onClick={() => toggleCharacter(character.filename)}
                  className={`selection-item ${character.selected ? 'selected' : ''}`}
                >
                  <div className="selection-avatar">
                    <img src={getInfluencerImageUrl(character.path)} alt={character.name} />
                  </div>
                  <span className="selection-name">{character.name}</span>
                  {character.selected && (
                    <div className="selection-check">
                      <i className="fas fa-check"></i>
                    </div>
                  )}
                </button>
              ))}
              {filteredCharacters.length === 0 && (
                <p className="empty-list">No characters found</p>
              )}
            </div>
          </div>

          {/* Environments Section */}
          <div className="selection-card">
            <h2>2. Select Environment</h2>
            <div className="search-box compact">
              <i className="fas fa-search"></i>
              <input
                type="text"
                placeholder="Search environments..."
                value={environmentSearch}
                onChange={(e) => setEnvironmentSearch(e.target.value)}
              />
            </div>
            <div className="selection-list">
              {filteredEnvironments.map((env) => (
                <button
                  key={env.filename}
                  onClick={() => toggleEnvironment(env.filename)}
                  className={`env-selection-item ${env.selected ? 'selected' : ''}`}
                >
                  <img src={getInfluencerImageUrl(env.path)} alt={env.name} />
                  <div className="env-selection-overlay">
                    <span>{env.name}</span>
                    {env.selected && (
                      <div className="selection-check small">
                        <i className="fas fa-check"></i>
                      </div>
                    )}
                  </div>
                </button>
              ))}
              {filteredEnvironments.length === 0 && (
                <p className="empty-list">No environments found</p>
              )}
            </div>
          </div>
        </div>

        {/* Middle Column - Video Preview & Scene Description */}
        <div className="scene-column">
          {generatedVideoPath && (
            <div className="video-result-card">
              <h2>Generated Video</h2>
              <div className="video-wrapper">
                <video
                  src={getInfluencerImageUrl(generatedVideoPath)}
                  controls
                  autoPlay
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          )}

          <div className="scene-card">
            <div className="scene-card-header">
              <h2>3. Describe Your Scene</h2>
              <div className="duration-toggle">
                <button
                  onClick={() => setVideoDuration(10)}
                  className={videoDuration === 10 ? 'active' : ''}
                >
                  10s
                </button>
                <button
                  onClick={() => setVideoDuration(30)}
                  className={videoDuration === 30 ? 'active' : ''}
                >
                  30s
                </button>
                <button
                  onClick={() => setVideoDuration(40)}
                  className={videoDuration === 40 ? 'active' : ''}
                >
                  40s
                </button>
              </div>
            </div>
            <textarea
              value={sceneDescription}
              onChange={(e) => setSceneDescription(e.target.value)}
              placeholder="Add action, dialogue, speakers, camera style, emotions, stage directions..."
              rows="12"
            />
            <p className="scene-hint">
              Include who speaks, what they say, how they feel, what happens, and any camera details. Selected: {videoDuration}s video
            </p>
          </div>
        </div>

        {/* Right Column - Scene Preview & Generate */}
        <div className="preview-column">
          <div className="preview-card">
            <div className="preview-card-header">
              <h2>4. Scene Preview</h2>
              <span className="validated-badge">
                <i className="fas fa-check-circle"></i> Validated
              </span>
            </div>

            <div className="preview-content">
              {error && (
                <div className="message error">
                  <i className="fas fa-exclamation-circle"></i>
                  {error}
                </div>
              )}

              {generationProgress && (
                <div className="message processing">
                  <i className="fas fa-spinner fa-spin"></i>
                  {generationProgress}
                </div>
              )}

              {enhancedPrompt && (
                <div className="enhanced-prompt">
                  <strong>AI Influencer Enhanced Scene:</strong>
                  <p>{enhancedPrompt}</p>
                </div>
              )}

              <div className="preview-info-block">
                <span className="preview-label">Environment</span>
                <p>AI-generated cinematic scene for influencer content.</p>
              </div>

              <div className="preview-info-block">
                <span className="preview-label">Camera Note</span>
                <p>Dynamic camera work optimized for social media engagement.</p>
              </div>

              <div className="preview-info-block">
                <span className="preview-label">Scene Details</span>
                <div className="scene-detail-card">
                  <p>
                    <strong>Scene 1:</strong> Selected Character (Based on prompt) — AI Influencer dialogue will be enhanced here...
                  </p>
                  <small>Scene context and responses generated by AI</small>
                </div>
              </div>
            </div>

            <button
              className="xbtn xbtn-xl xbtn-primary"
              onClick={handleGenerateVideo}
              disabled={isGenerating || !sceneDescription.trim() || !characters.find(c => c.selected) || !environments.find(e => e.selected) || (!isAdmin && credits < 1)}
            >
              {isGenerating ? (
                <><i className="fas fa-spinner fa-spin"></i> Generating {videoDuration}s Video...</>
              ) : (!isAdmin && credits < 1) ? (
                <><i className="fas fa-lock"></i> No Credits Remaining</>
              ) : (
                <><i className="fas fa-play"></i> Generate {videoDuration}s Video {!isAdmin && <span style={{opacity: 0.7, fontSize: '12px', marginLeft: '4px'}}>({credits} credits left)</span>}</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InfluencerPrompt;
